import { MOVEMENT_STATUS_PRESENTATION, getTimeGap } from "../utils";
// Canonical movement resolution shared with the sidebar list (ListCard). Reusing
// it here guarantees the summary shown above the data table matches the sidebar
// row exactly (same speed, status and freshness).
import { formatCanonicalSpeed, getListStatusTime, resolveListCardMovement } from "./listCardMovement";
import { vehiclePlaceholder, devicePlaceholder } from "../../../assets";
import { getLocalizedString } from "../../../shared/translation";

const getFallbackSpeedMps = (rawSpeed, displaySpeed) => {
  if (rawSpeed !== undefined && rawSpeed !== null && rawSpeed !== "") {
    return rawSpeed;
  }

  if (typeof displaySpeed === "number") {
    return displaySpeed;
  }

  if (typeof displaySpeed !== "string") {
    return undefined;
  }

  const numericSpeed = Number(displaySpeed.match(/-?\d+(\.\d+)?/)?.[0]);
  if (!Number.isFinite(numericSpeed)) {
    return undefined;
  }

  return displaySpeed.toLowerCase().includes("km/h") ? numericSpeed / 3.6 : numericSpeed;
};

/**
 * Presents the status / plate number / speed of the currently
 * selected asset above the data table, mirroring how a sidebar `ListCard` row
 * looks. Rendered for both Vehicles and Devices (and the alternative layout,
 * which reuses the same containers).
 *
 * It uses its OWN scoped class names (prefixed `selected-asset-summary`) so it
 * never collides with the track-points table styling; the columns still line up
 * with the sidebar because they share the same widths in the stylesheet.
 *
 * @param {Object} props
 * @param {Object} props.data The selected asset row (same shape as a ListCard item).
 * @param {"Vehicles"|"Devices"} [props.cardType]
 * @param {Object} [props.mapData] Realtime map data keyed by device/source id.
 * @param {Object} [props.telemetry] Canonical getters from `useVehicleTelemetry`.
 */
export const SelectedAssetSummary = ({
  data,
  cardType = "Vehicles",
  mapData,
  telemetry,
  // The currently selected plate. Reliably set on every selection path (sidebar
  // click AND map marker click), so it is the authoritative key for the live
  // telemetry lookup and for the plate label, even when `data` is a stale/partial
  // snapshot captured at click time.
  plateNumber: plateNumberProp,
  // "full" (default) renders the status / plate / speed summary used
  // on the LIVE tab. "plate" renders only the plate number (History tab), sitting
  // on the same line as the Column Configuration button.
  variant = "full",
}) => {
  if (!data && !plateNumberProp) return null;

  const {
    plate_number: plateFromData,
    device_name_list,
    device_name,
    device_id,
    ignition,
    speed,
    rawSpeed,
    lastUpdated,
    source_time,
    source_id,
    movementStatus,
  } = data || {};

  // Prefer the explicit selected plate so the telemetry lookup and label always
  // match the sidebar, regardless of how the asset was selected.
  const plate_number = plateNumberProp || plateFromData;

  // History tab: show only the plate number, aligned on the same line as the
  // Column Configuration button. No status/speed/ignition columns.
  if (variant === "plate") {
    const plateOnlyValue =
      cardType === "Devices"
        ? device_name_list || device_name || plate_number || "-"
        : plate_number || "-";

    return (
      <div className="selected-asset-summary selected-asset-summary--plate">
        <h3 className="selected-asset-summary_plate truncate" title={plateOnlyValue}>
          {plateOnlyValue}
        </h3>
      </div>
    );
  }

  const liveMapData = mapData
    ? mapData[device_name_list] || mapData[device_id] || mapData[source_id] || {}
    : {};
  const hasLiveStatusData =
    liveMapData.statusTime !== undefined ||
    liveMapData.sourceTime ||
    liveMapData.serverTime ||
    liveMapData.ignition !== undefined ||
    liveMapData.rawSpeed !== undefined ||
    liveMapData.speed !== undefined;
  const fallbackIgnition = liveMapData.ignition ?? ignition;
  const fallbackTime = getListStatusTime(cardType, liveMapData, lastUpdated, source_time);
  const fallbackSpeed = getFallbackSpeedMps(
    liveMapData.rawSpeed ?? rawSpeed,
    liveMapData.speed || speed
  );

  const canonical = resolveListCardMovement({
    telemetry,
    plateNumber: plate_number,
    fallback: {
      observedAt: fallbackTime,
      deviceDataAt: lastUpdated,
      rawSpeed: fallbackSpeed,
      ignition: fallbackIgnition,
      movementStatus: hasLiveStatusData ? undefined : movementStatus,
    },
  });

  const statusTime = canonical.observedAt;
  const resolvedMovementStatus = canonical.movementStatus;
  const displaySpeed = canonical.isCanonical
    ? formatCanonicalSpeed(canonical.rawSpeed)
    : speed || null;
  const normalizedMovementStatus =
    resolvedMovementStatus === "stopped" ? "parked" : resolvedMovementStatus;
  const movementStatusPresentation =
    MOVEMENT_STATUS_PRESENTATION[normalizedMovementStatus] ||
    MOVEMENT_STATUS_PRESENTATION.offline;
  const movementStatusLabel = getLocalizedString(
    movementStatusPresentation.localizationKey,
    movementStatusPresentation.defaultLabel
  );
  const { activeWithin } = getTimeGap(statusTime) || {};

  const firstColumnLabel = getLocalizedString("status", "Status");
  const secondColumnLabel =
    cardType === "Devices"
      ? getLocalizedString("device_id", "Device ID")
      : getLocalizedString("plate_numner", "Plate Number");
  const thirdColumnLabel =
    cardType === "Devices"
      ? getLocalizedString("device_name", "Device Name")
      : getLocalizedString("speed", "Speed");
  const secondColumnValue =
    cardType === "Devices"
      ? device_name_list || device_name || plate_number || "-"
      : plate_number || "-";
  const thirdColumnValue = cardType === "Devices" ? device_name || "-" : displaySpeed || "-";

  return (
    <div className="on-details-selected-asset-summary">
        {/* Status */}
        <div
          className={`selected-asset-summary_col status ${normalizedMovementStatus}`}
          aria-label={`${firstColumnLabel}: ${movementStatusLabel}`}
          title={movementStatusLabel}
        >

          <span className="selected-asset-summary_status-icon">
            <img
              className="user-profile-image"
              alt={movementStatusLabel}
              src={cardType === "Vehicles" ? vehiclePlaceholder : devicePlaceholder}
            />
          </span>
          <span className="selected-asset-summary_active-within">{activeWithin}</span>
        </div>

        {/* Plate number / device id */}
        <div
          className="assets-info"
          aria-label={`${secondColumnLabel}: ${secondColumnValue}`}
        >

          <strong className="" title={secondColumnValue}>
            {secondColumnValue}
          </strong>
        </div>

        {/* Speed (Vehicles) / device name (Devices) */}
        <div
          className="selected-asset-summary_col value"
          aria-label={`${thirdColumnLabel}: ${thirdColumnValue}`}
        >

          {cardType === "Devices" ? (
            <div className="truncate" title={thirdColumnValue}>
              {thirdColumnValue}
            </div>
          ) : (
            <div title={thirdColumnValue}>{thirdColumnValue}</div>
          )}
        </div>
    </div>
  );
};

export default SelectedAssetSummary;
